import React from "react";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { usePatientSelectionForCopilotModalViewModel } from "./usePatientSelectionForCopilotModalViewModel";
import {
	ModalPaper,
	ModalTop,
	ModalTitle,
	CloseButton,
	ModalDescription,
	ModalContent,
	PatientSearch,
	PatientList,
	PatientItem,
	PatientSummary,
	ListAvatar,
	PatientInfo,
	ListName,
	OpenCopilotButton,
	LoadMoreButton,
	LoadMoreSpinnerContainer,
	MiniSpinner,
	NoResults,
	SkeletonRow,
} from "./styles";

import { ReactComponent as SparkIcon } from "../../../../assets/kinologyIcons/svg/Star4.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/kinologyIcons/svg/search.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/kinologyIcons/svg/close.svg";
import { CopilotPatientReference } from "../../services/clinicalDashboardService";

const useStyles = makeStyles(() =>
	createStyles({
		modal: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
	})
);

interface PatientSelectionForCopilotModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSelectPatient: (patient: CopilotPatientReference) => void;
}

const PatientSelectionForCopilotModal: React.FC<
	PatientSelectionForCopilotModalProps
> = ({ isOpen, onClose, onSelectPatient }) => {
	const classes = useStyles();
	const {
		searchQuery,
		filteredPatients,
		hasExtraPatients,
		patientsLoading,
		loadingMore,
		error,
		handleSearchChange,
		handleSelect,
		handleLoadMore,
		retry,
	} = usePatientSelectionForCopilotModalViewModel(
		isOpen,
		onClose,
		onSelectPatient
	);

	return (
		<Modal
			aria-labelledby="copilot-patient-selection-modal-title"
			aria-describedby="copilot-patient-selection-modal-description"
			className={classes.modal}
			open={isOpen}
			onClose={onClose}
			closeAfterTransition
			BackdropComponent={Backdrop}
			BackdropProps={{
				timeout: 300,
			}}
		>
			<Fade in={isOpen}>
				<ModalPaper>
					<ModalTop>
						<ModalTitle id="copilot-patient-selection-modal-title">
							Lista de Pacientes
						</ModalTitle>
						<CloseButton type="button" onClick={onClose} aria-label="Fechar">
							<CloseIcon width={15} height={15} />
						</CloseButton>
					</ModalTop>

					<ModalDescription>
						<p id="copilot-patient-selection-modal-description">
							Lista de pacientes
						</p>
					</ModalDescription>

					<ModalContent>
						<PatientSearch>
							<SearchIcon />
							<input
								type="search"
								value={searchQuery}
								onChange={(e) => handleSearchChange(e.target.value)}
								placeholder="Pesquisar paciente"
								aria-label="Pesquisar paciente"
							/>
						</PatientSearch>

						{patientsLoading && filteredPatients.length === 0 ? (
							<PatientList>
								<SkeletonRow />
								<SkeletonRow />
								<SkeletonRow />
								<SkeletonRow />
							</PatientList>
						) : error ? (
							<NoResults role="alert">
								<div>{error}</div>
								<button type="button" onClick={retry}>
									Tentar novamente
								</button>
							</NoResults>
						) : filteredPatients.length > 0 ? (
							<PatientList>
								{filteredPatients.map((p) => (
									<PatientItem key={p.id}>
										<PatientSummary>
											<ListAvatar>
												{p.photoUrl ? (
													<img src={p.photoUrl} alt={`Foto de ${p.name}`} />
												) : (
													p.initials
												)}
											</ListAvatar>
											<PatientInfo>
												<ListName>{p.name}</ListName>
											</PatientInfo>
											<OpenCopilotButton
												type="button"
												onClick={() => handleSelect(p)}
												aria-label={`Selecionar ${p.name} e abrir no Copiloto Clínico IA`}
											>
												<SparkIcon />
												Abrir Copiloto Clínico IA
											</OpenCopilotButton>
										</PatientSummary>
									</PatientItem>
								))}
							</PatientList>
						) : (
							<NoResults>Nenhum paciente encontrado.</NoResults>
						)}

						{loadingMore ? (
							<LoadMoreSpinnerContainer>
								<MiniSpinner />
							</LoadMoreSpinnerContainer>
						) : hasExtraPatients ? (
							<LoadMoreButton type="button" onClick={handleLoadMore}>
								Carregar mais 8 pacientes
							</LoadMoreButton>
						) : null}
					</ModalContent>
				</ModalPaper>
			</Fade>
		</Modal>
	);
};

export default PatientSelectionForCopilotModal;
